<template>
  <div class="mt-8" v-show="showDetails">
    <dispute-notification v-if="stageDispute">
      {{ stageDispute.comment }}
    </dispute-notification>

    <el-form
      @keyup.enter.native="validateForm('kycDetailsForm')"
      @submit.native.prevent="validateForm('kycDetailsForm')"
      label-position="top"
      :model="kycDetailsForm"
      ref="kycDetailsForm"
      :rules="rules"
    >
      <div class="flex flex-col gap-1 bg-white">
        <el-form-item class="w-full" prop="accountPurpose">
          <p>
            What is the intended purpose of your account with {{ tenantLabel }}
            <span class="text-error-10">*</span>
          </p>
          <el-input v-model="kycDetailsForm.accountPurpose"></el-input>
        </el-form-item>
        <el-form-item
          prop="expectedTransactionCurrency"
          @click.native="toggleExpectedTransactionCurrency"
        >
          <p>
            Expected Transaction Currencies with {{ tenantLabel }} ?
            <span class="text-error-10">*</span>
          </p>
          <div
            class="flex items-center justify-between rounded px-4 py-2 border cursor-pointer"
          >
            <div class="flex flex-wrap gap-y-3 items-center gap-x-2">
              <span
                v-if="kycDetailsForm.expectedTransactionCurrency.length === 0"
                class="font-400 opacity-30"
                >Select</span
              >
              <template v-else>
                <span
                  v-for="(
                    currency, index
                  ) in kycDetailsForm.expectedTransactionCurrency"
                  :key="index"
                  class="flex items-center gap-x-3 rounded shadow-sm bg-gray-6 px-2.5 py-1.5 relative z-20 duration-300 transition-all hover:bg-gray-2"
                >
                  <currency-icons
                    :currency="getCurrencyCountry(currency)"
                    size="h-5 w-5"
                  />
                  <span class="text-sm uppercase">{{ currency }}</span>
                  <close-icon
                    size="h-4 w-5"
                    @click.native.stop="removeLocationHandler(currency)"
                  />
                </span>
              </template>
            </div>
            <arrow-icon
              color="#5D5E68"
              size="h-7 w-7"
              class="transform rotate-90"
              :class="{ '-rotate-90': showTransactionCurrencyDropdown }"
            />
          </div>
        </el-form-item>

        <!-- custom dropdown -->
        <div
          v-if="showTransactionCurrencyDropdown"
          class="rounded-lg flex flex-col items-center justify-center px-4 py-3.5 border cursor-pointer"
        >
          <el-input
            placeholder="Search"
            prefix-icon="el-icon-search"
            class="mx-4 mt-1"
            v-model.trim="searchSupportedFiatTerm"
            debounce
          />
          <!-- currency option -->
          <div class="w-full h-24 overflow-scroll">
            <el-checkbox-group
              v-model="kycDetailsForm.expectedTransactionCurrency"
            >
              <el-checkbox
                class="flex items-center mt-6 relative"
                v-for="(fiat, index) in filteredSupportedFiats"
                :key="index"
                :label="fiat.currency"
              >
                <div class="flex items-center justify-between w-full">
                  <p class="flex items-center gap-x-2">
                    <currency-icons :currency="fiat.name" />
                    <span>{{ fiat.currencyFull }}</span>
                  </p>
                  <p class="absolute right-3 top-0">{{ fiat.currency }}</p>
                </div>
              </el-checkbox>
            </el-checkbox-group>
          </div>
        </div>

        <el-form-item class="w-full" prop="sourceOfFunds">
          <p>Source of funds or wealth <span class="text-error-10">*</span></p>
          <el-select
            class="w-full"
            v-model="kycDetailsForm.sourceOfFunds"
            placeholder="Select"
          >
            <el-option
              v-for="(source, index) in sourceOfFunds"
              :key="index"
              :label="source.label"
              :value="source.value"
            >
              {{ source.label }}
            </el-option>
          </el-select>
        </el-form-item>

        <div class="flex flex-col md:flex-row gap-4">
          <el-form-item prop="expectedMonthlyTurnover" class="w-full md:w-1/2">
            <p>
              Expected Monthly Turnover <span class="text-error-10">*</span>
            </p>
            <el-select
              class="w-full"
              v-model="kycDetailsForm.expectedMonthlyTurnover"
              placeholder="Select"
            >
              <el-option
                v-for="(turnover, index) in transactionTurnover"
                :key="index"
                :label="turnover.label"
                :value="turnover.value"
              >
                {{ turnover.value }}
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item prop="expectedAnnualTurnover" class="w-full md:w-1/2">
            <p>Expected Annual Turnover <span class="text-error-10">*</span></p>
            <el-select
              class="w-full"
              v-model="kycDetailsForm.expectedAnnualTurnover"
              placeholder="Select"
            >
              <el-option
                v-for="(turnover, index) in transactionTurnover"
                :key="index"
                :label="turnover.label"
                :value="turnover.value"
              >
                {{ turnover.value }}
              </el-option>
            </el-select>
          </el-form-item>
        </div>
        <el-form-item prop="monthlyTransaction" class="w-full">
          <p>
            Number of Monthly Transactions <span class="text-error-10">*</span>
          </p>
          <el-select
            class="w-full"
            v-model="kycDetailsForm.monthlyTransaction"
            placeholder="Select"
          >
            <el-option
              v-for="(turnover, index) in monthlyTransaction"
              :key="index"
              :label="turnover.label"
              :value="turnover.value"
            >
              {{ turnover.label }}
            </el-option>
          </el-select>
        </el-form-item>

        <button
          class="text-white bg-primary rounded-lg mt-2 h-12 md:h-14 w-44 md:w-52 hover:opacity-90"
          :disabled="isLoading || isApproved"
          type="submit"
        >
          <spinner
            v-if="isLoading"
            size="small"
            :spacing="40"
            :line-fg-color="$primaryColor"
          />
          <span v-else>Confirm</span>
        </button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { FIAT, MonthlyTransaction } from '@/utils/constants.js'
import {
  ParseAmount,
  ValidateKycInput,
  GetCurrencyCountry
} from '@/utils/helpers.js'
import submitKycDetailsStep from '@/mixins/kyc.js'
import tenant from '@/mixins/tenant.js'

export default {
  name: 'TransactionProfile',
  mixins: [submitKycDetailsStep, tenant],
  components: {
    DisputeNotification: () =>
      import('@/components/v2/completeKycForm/common/DisputeNotification.vue'),
    ArrowIcon: () => import('@/assets/v2/icons/ArrowRightIcon.vue'),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    CloseIcon: () => import('@/assets/v2/icons/CloseIcon.vue')
  },
  data () {
    return {
      isLoading: false,
      fiatCurrencies: FIAT,
      kycStep: 2,
      sourceOfFunds: [
        {
          label: 'Personal',
          value: 'personal'
        },
        {
          label: 'Business',
          value: 'business'
        }
      ],
      kycDetailsForm: {
        expectedTransactionCurrency: [],
        accountPurpose: '',
        transactionCurrency: '',
        sourceOfFunds: '',
        expectedMonthlyTurnover: '',
        expectedAnnualTurnover: '',
        monthlyTransaction: ''
      },
      rules: {
        accountPurpose: [
          {
            required: true,
            message: 'Please input purpose of your account',
            trigger: ['blur', 'change']
          },
          { validator: ValidateKycInput, trigger: ['blur', 'change'] }
        ],
        transactionCurrency: [
          {
            required: true,
            message: 'Please select transaction currency',
            trigger: ['blur', 'change']
          }
        ],
        sourceOfFunds: [
          {
            required: true,
            message: 'Please input source of funds',
            trigger: ['blur', 'change']
          },
          { validator: ValidateKycInput, trigger: ['blur', 'change'] }
        ],
        expectedMonthlyTurnover: [
          {
            required: true,
            message: 'Please enter monthly turnover',
            trigger: ['blur', 'change']
          }
        ],
        expectedAnnualTurnover: [
          {
            required: true,
            message: 'Please enter annual turnover',
            trigger: ['blur', 'change']
          }
        ],
        monthlyTransaction: [
          {
            required: true,
            message: 'Please enter monthly transactions',
            trigger: ['blur', 'change']
          }
        ],
        expectedTransactionCurrency: [
          {
            required: true,
            message: 'Please input expected transaction currency',
            trigger: ['blur', 'change']
          }
        ]
      },
      supportedFiats: FIAT,
      showTransactionCurrencyDropdown: false,
      searchSupportedFiatTerm: '',
      transactionTurnover: [
        {
          label: 'Above $100,000',
          value: 'Above $100,000'
        },
        {
          label: '$100,000 - $500,000',
          value: '$100,000 - $500,000'
        },
        {
          label: '$500,000 - $1,000,000',
          value: '$500,000 - $1,000,000'
        },
        {
          label: 'Above $1,000,000',
          value: 'Above $1,000,000'
        }
      ],
      monthlyTransaction: MonthlyTransaction
    }
  },
  props: {
    business: {
      type: Object,
      default: () => {}
    }
  },
  computed: {
    showDetails () {
      return this.business.show && this.business.step === this.kycStep
    },
    tenantLabel () {
      return this.tenantTitle
    },
    transactionInfo () {
      return this.$store.state.kyc.kycDetails
    },
    isApproved () {
      return this.approvedStages.includes(this.business.step)
    },
    filteredSupportedFiats () {
      if (!this.searchSupportedFiatTerm.trim()) {
        return this.filteredCurrencyOptions
      }

      const searchTerm = this.searchSupportedFiatTerm.trim().toLowerCase()

      return this.filteredCurrencyOptions.filter((currency) => {
        const {
          name,
          symbol,
          currency: code,
          currencyFull,
          shortCode
        } = currency

        return [name, symbol, code, currencyFull, shortCode].some((field) =>
          field?.toLowerCase().includes(searchTerm)
        )
      })
    }
  },
  methods: {
    removeLocationHandler (currency) {
      this.kycDetailsForm.expectedTransactionCurrency =
        this.kycDetailsForm.expectedTransactionCurrency.filter(
          (selectedCurrency) => selectedCurrency !== currency
        )
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    validateForm (kycDetailsForm) {
      this.$refs[kycDetailsForm].validate((valid) => {
        if (valid) {
          this.submitTransactionProfile()
        } else {
          this.$notify.error({
            title: 'Incomplete details',
            message: 'Please fill the missing information',
            position: 'top-right'
          })
        }
      })
    },
    submitTransactionProfile () {
      const transactionProfile = {
        accountPurpose: this.kycDetailsForm.accountPurpose,
        transactionCurrencies: this.kycDetailsForm.expectedTransactionCurrency,
        sourceOfFunds: this.kycDetailsForm.sourceOfFunds,
        expectedMonthlyTurnover: this.kycDetailsForm.expectedMonthlyTurnover,
        expectedAnnualTurnover: this.kycDetailsForm.expectedAnnualTurnover,
        monthlyTurnover: this.kycDetailsForm.monthlyTransaction
      }
      const payload = {
        kycDetailsForm: { transactionProfile },
        kycStep: this.kycStep
      }
      this.submitKycDetailsStep(payload)
    },
    prefillForm () {
      const transaction = this.transactionInfo

      this.kycDetailsForm = {
        ...this.kycDetailsForm,
        accountPurpose: transaction.accountPurpose || '',
        expectedTransactionCurrency: transaction.transactionCurrencies || [],
        sourceOfFunds: transaction.sourceOfFunds || '',
        expectedMonthlyTurnover: transaction.expectedMonthlyTurnover || '',
        expectedAnnualTurnover: transaction.expectedAnnualTurnover || '',
        monthlyTransaction: transaction.monthlyTurnover || ''
      }
    },
    validateExpectedTransactionCurrency () {
      this.$refs.kycDetailsForm.validateField('expectedTransactionCurrency')
    },
    toggleExpectedTransactionCurrency () {
      this.showTransactionCurrencyDropdown =
        !this.showTransactionCurrencyDropdown
    }
  },
  watch: {
    'kycDetailsForm.expectedTransactionCurrency': {
      handler: function () {
        this.$nextTick(() => {
          this.validateExpectedTransactionCurrency()
        })
      },
      immediate: true
    }
  },
  mounted () {
    if (this.transactionInfo) {
      this.prefillForm()
    }
  }
}
</script>

<style scoped lang="scss">
p {
  color: #5d5e68;
  font-size: 13px;
}
button:disabled {
  opacity: 0.6;
  cursor: default;
}
:deep(.el-checkbox__inner) {
  height: 1.3rem;
  width: 1.3rem;
}
:deep(.el-checkbox__inner::after) {
  height: 0.6rem;
  left: 0.4rem;
  border-width: 2px;
  top: 0.15rem;
}
:deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
  color: var(--color-primary);
}
</style>
